import React, { useEffect, useState } from "react";
import { CloseButton, Col, Dropdown, Spinner, Table } from "react-bootstrap";
import useGetUrlLocale from "../../hooks/useGetUrlLocale";
import { useDebounceEffect } from "../../hooks/useDebounceEffect";
import ResetPasswordModal from "./modal/ResetPasswordModal";
import { Search, ThreeDotsVertical } from "react-bootstrap-icons";
import {
  useGenerateDisputeMutation,
  useSetRegimeImpersonateMutation,
  useUsersWithPaginationQuery,
} from "../../graphql/userAdministration.hooks";
import { GraphUser } from "../../../graphql/types";
import { BlockUserModal } from "../../components/modals/blockUserModal";
import { useUserBlockMutation } from "../../graphql/menu.hooks";
import Pagination from "../../components/pagination";
import { useTranslation } from "react-i18next";
import { useReactiveVar } from "@apollo/client";
import { editPremium } from "../../graphql/state";
import { EditPremiumModal } from "../../components/modals/editPremiumModal";
import { DeepPartial } from "../../types";

const UserAdministration = () => {
  const { t } = useTranslation();
  const lng = useGetUrlLocale();

  const editPremiumModal = useReactiveVar(editPremium);

  const [isResetModalOpen, setIsResetModalOpen] = useState<boolean>(false);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [selectedUser, setSelectedUser] =
    useState<DeepPartial<GraphUser>>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(12);

  const {
    data: usersData,
    loading: usersDataLoading,
    refetch: refetchUsersData,
  } = useUsersWithPaginationQuery({
    variables: { limit: limit, page: currentPage, search: searchTerm },
    fetchPolicy: "network-only",
  });
  const [
    regimeImpersonate,
    { loading: regimeImpersonateLoading, data: impersonateData },
  ] = useSetRegimeImpersonateMutation({
    onCompleted: () => {
      window.location.href = `${window.location.origin}${lng}/projects`;
    },
  });

  const pagination = usersData?.usersWithPagination?.paginator;
  const pagesCount = pagination?.totalPages || 0;
  const totalItems = pagination?.totalItemCount || 0;
  const firstItemIndex = pagination
    ? (currentPage - 1) * pagination.itemNumberPerPage + 1
    : 0;
  const lastItemIndex = pagination
    ? Math.min(currentPage * pagination.itemNumberPerPage, totalItems)
    : 0;

  const [unBlock] = useUserBlockMutation({
    onCompleted: () => {
      refetchUsersData();
    },
  });
  const [generateDispute] = useGenerateDisputeMutation({
    onCompleted: () => {
      refetchUsersData();
    },
  });

  const handleSearch = async () => {
    setCurrentPage(1);
    refetchUsersData({
      page: currentPage,
      search: searchTerm,
      limit,
    });
  };

  useDebounceEffect(handleSearch, 500, [searchTerm]);

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
    refetchUsersData({
      page: pageNumber,
      search: searchTerm,
      limit,
    });
  };

  const handleLimitChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newLimit = Number(event.target.value);
    setLimit(newLimit);
    setCurrentPage(1);
    refetchUsersData({
      search: searchTerm,
      page: 1,
      limit: newLimit,
    });
  };

  useEffect(() => {
    document.title = "User Administration";
  }, []);

  const tryUnBlockUser = async (user: DeepPartial<GraphUser>) => {
    try {
      await unBlock({
        variables: { userId: user?.id, status: false, comment: "" },
      });
    } catch (error) {
      console.error(error);
    }
  };

  const openResetModal = () => setIsResetModalOpen(true);
  const closeResetModal = () => setIsResetModalOpen(false);

  const openBlockModal = () => setShowBlockModal(true);
  const closeBlockModal = () => setShowBlockModal(false);

  const selectUser = (user: DeepPartial<GraphUser>) => setSelectedUser(user);
  const unselectUser = () => setSelectedUser(null);

  const handleImpersonate = (user: DeepPartial<GraphUser>) => {
    regimeImpersonate({
      variables: { userId: user?.id },
    });
  };

  const openEditPremiumModal = (user: DeepPartial<GraphUser>) => {
    editPremium({ isActive: true, user });
  };

  const tryGenerateDispute = async (user: DeepPartial<GraphUser>) => {
    try {
      await generateDispute({
        variables: { userId: user?.id },
      });
    } catch (error) {
      console.error(error);
    }
  };

  if (regimeImpersonateLoading || impersonateData)
    return <Spinner animation="border" role="status"></Spinner>;

  return (
    <>
      {isResetModalOpen && (
        <ResetPasswordModal
          isShow={isResetModalOpen}
          handleCloseModal={() => {
            unselectUser();
            closeResetModal();
          }}
          selectedUser={selectedUser}
        />
      )}
      {showBlockModal && (
        <BlockUserModal
          showModal={showBlockModal}
          refetch={refetchUsersData}
          handleCloseModal={() => {
            unselectUser();
            closeBlockModal();
          }}
          selectedUser={selectedUser}
        />
      )}
      <div className="container">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div className="mb-4">
            <h2>User Administration</h2>
          </div>
        </div>

        <Col
          xs={12}
          className="mb-4 bg-white rounded py-3 px-4 d-flex flex-wrap align-items-center justify-content-between"
        >
          <h5 className="text-dark fs-4 fw-bold">All Users</h5>
          <div className="templateSearch d-flex align-items-center px-2 pb-2 me-4">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by Email"
              className="templateInput text-gray me-3 w-100"
            />
            {searchTerm.length > 0 && (
              <CloseButton
                className="text-dark me-2"
                onClick={() => setSearchTerm("")}
              />
            )}
            <Search className="text-dark" />
          </div>
        </Col>

        <div className="g-4 pt-5 bg-white rounded">
          <div className="px-3 pb-3">
            <Table className="table table-responsive table-borderless">
              <thead>
                <tr>
                  <th className="fs-5">ID</th>
                  <th className="fs-5">Email</th>
                  <th className="fs-5 text-center">Premium</th>
                  <th className="fs-5 text-center">User management</th>
                </tr>
              </thead>
              <tbody>
                <>
                  {usersData?.usersWithPagination?.users?.map((user) => (
                    <tr
                      key={user.id}
                      style={{ lineHeight: "2.5", height: "50px" }}
                    >
                      <td style={{ width: "75px" }} className="text-center">
                        {user.id}
                      </td>
                      <td className="text-nowrap fs-5">{user.email}</td>
                      <td className="text-break text-center">
                        <button
                          onClick={() => openEditPremiumModal(user)}
                          className="btn btn-white px-5 text-dark rounded gray-hover"
                        >
                          {user.activePremium ? "Yes" : "No"}
                        </button>
                      </td>
                      <td className="d-flex justify-content-center">
                        <Dropdown className="administrationDropdown text-center">
                          <Dropdown.Toggle
                            id="administration-options"
                            variant="white"
                          >
                            <ThreeDotsVertical className="fs-6 text-dark" />
                          </Dropdown.Toggle>
                          <Dropdown.Menu className="w-100 fs-7">
                            <Dropdown.Item
                              className="text-dark fs-7"
                              onClick={() => handleImpersonate(user)}
                            >
                              Impersonate
                            </Dropdown.Item>
                            <Dropdown.Item
                              className="text-dark fs-7"
                              onClick={() => {
                                selectUser(user);
                                openResetModal();
                              }}
                            >
                              Reset Password
                            </Dropdown.Item>
                            <Dropdown.Item
                              className="text-dark fs-7"
                              onClick={() => tryGenerateDispute(user)}
                            >
                              Generate dispute
                            </Dropdown.Item>
                            <Dropdown.Item
                              as="a"
                              href={user?.userDispute?.path}
                              download
                              disabled={!user?.userDispute?.path}
                              className={`text-dark fs-7 ${!user?.userDispute?.path && "opacity-50"}`}
                            >
                              Download dispute
                            </Dropdown.Item>
                            {!user?.userBlock?.status ? (
                              <Dropdown.Item
                                className="text-danger fs-7"
                                onClick={() => {
                                  selectUser(user);
                                  openBlockModal();
                                }}
                              >
                                Block
                              </Dropdown.Item>
                            ) : (
                              <Dropdown.Item
                                className="text-success fs-7"
                                onClick={() => {
                                  tryUnBlockUser(user);
                                }}
                              >
                                Unblock
                              </Dropdown.Item>
                            )}
                          </Dropdown.Menu>
                        </Dropdown>
                      </td>
                    </tr>
                  ))}
                </>
              </tbody>
            </Table>
            {usersDataLoading && (
              <div className="d-flex align-items-center justify-content-center my-4">
                <Spinner animation="border" role="status" />
              </div>
            )}
            {!usersData?.usersWithPagination?.users?.length &&
              !usersDataLoading &&
              !regimeImpersonateLoading && (
                <div className="text-center my-4">No users found</div>
              )}
          </div>
          <div className="col-12 d-flex justify-content-between align-items-center bg-white px-3 px-md-5 py-3 mt-5 rounded">
            <Pagination
              t={t}
              pages={pagesCount}
              currentPage={currentPage}
              onPageChange={handlePageChange}
              firstItemIndex={firstItemIndex}
              lastItemIndex={lastItemIndex}
              totalItems={totalItems}
              limit={limit}
              onLimitChange={handleLimitChange}
            />
          </div>
        </div>
      </div>
      <EditPremiumModal
        showModal={editPremiumModal.isActive}
        handleCloseModal={() => editPremium({ isActive: false, user: null })}
        refetchUsers={refetchUsersData}
      />
    </>
  );
};

export default UserAdministration;
